import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowRight,
  FileText,
  Calendar,
  User,
  PenTool,
  UploadCloud,
  Download,
  CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContractDetails({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [id]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${id}`);
      setContract(response.data);
    } catch (error) {
      toast.error('فشل تحميل تفاصيل العقد');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('الرجاء اختيار ملف PDF');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      await axios.post(`${API}/contracts/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('تم رفع الملف بنجاح');
      setIsUploadDialogOpen(false);
      setSelectedFile(null);
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل رفع الملف');
    } finally {
      setUploading(false);
    }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      // مجرد تغيير حالة بدون توقيع فعلي
      await axios.post(`${API}/contracts/${id}/sign`, { 
        signature_data: 'signed' 
      });
      toast.success('تم توقيع العقد بنجاح');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل توقيع العقد');
    } finally {
      setSigning(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/contracts/${id}/approve`);
      toast.success('تم اعتماد العقد بنجاح');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل اعتماد العقد');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { text: 'مسودة', class: 'status-draft' },
      review: { text: 'قيد المراجعة', class: 'status-review' },
      approved: { text: 'معتمد', class: 'status-approved' },
      signed: { text: 'موقّع', class: 'status-signed' },
    };
    const statusInfo = statusMap[status] || statusMap.draft;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!contract) {
    return null;
  }

  const isLegalManager = user?.role === 'legal_manager';
  const canUpload = isLegalManager;
  const canApprove = isLegalManager && contract.status === 'review';
  const canSign = isLegalManager && contract.status === 'approved';

  return (
    <div className="min-h-screen bg-slate-50 p-8" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowRight size={20} className="ml-2" />
            العودة
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">{contract.title}</h1>
              <p className="text-slate-600">{contract.description}</p>
            </div>
            {getStatusBadge(contract.status)}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contract Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-right">معلومات العقد</CardTitle>
                <CardDescription className="text-right">التفاصيل الأساسية للعقد</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <FileText className="text-slate-500" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">نوع العقد</p>
                      <p className="font-medium text-slate-900">{contract.contract_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="text-slate-500" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">الطرف الثاني</p>
                      <p className="font-medium text-slate-900">{contract.party_name}</p>
                    </div>
                  </div>
                  {contract.contract_value && (
                    <div className="flex items-center gap-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_legal-dashboard-24/artifacts/wv6i3s4h_950_5ca67b84db.png"
                        alt="SAR"
                        className="w-7 h-7 object-contain"
                        style={{ filter: 'opacity(0.6)' }}
                      />
                      <div>
                        <p className="text-xs text-slate-500">قيمة العقد</p>
                        <p className="font-medium text-slate-900">
                          {contract.contract_value.toLocaleString('ar-SA')} ر.س
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">* لا يشمل ضريبة القيمة المضافة</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Calendar className="text-slate-500" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">تاريخ الإنشاء</p>
                      <p className="font-medium text-slate-900">
                        {new Date(contract.created_at).toLocaleDateString('ar-SA')}
                      </p>
                    </div>
                  </div>
                </div>
                {contract.notes && (
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">ملاحظات</p>
                    <p className="text-slate-700">{contract.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PDF Document */}
            {contract.file_name && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-right">المستند</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-3">
                      <FileText className="text-slate-600" size={24} />
                      <div>
                        <p className="font-medium text-slate-900">{contract.file_name}</p>
                        <p className="text-xs text-slate-500">ملف PDF</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`/api/files/contract/${id}`, '_blank')}
                      data-testid="download-pdf-button"
                    >
                      <Download size={16} className="ml-2" />
                      تحميل
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Signature Info - Simple Text */}
            {contract.status === 'signed' && contract.signed_at && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-right flex items-center gap-2">
                    <CheckCircle2 className="text-emerald-600" size={24} />
                    العقد موقّع
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-emerald-50 rounded-lg border-2 border-emerald-200 p-6">
                    <div className="text-center space-y-2">
                      <div className="text-4xl">✓</div>
                      <p className="text-lg font-bold text-emerald-800">تم توقيع العقد</p>
                      <p className="text-sm text-slate-600">
                        تاريخ التوقيع: {new Date(contract.signed_at).toLocaleString('ar-SA')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Actions Sidebar - Only for Legal Manager */}
          <div className="space-y-6">
            {isLegalManager && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-right">الإجراءات</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {canUpload && !contract.file_name && (
                    <Button
                      onClick={() => setIsUploadDialogOpen(true)}
                      className="w-full bg-[#E94B7B] hover:bg-[#d43a6a]"
                      data-testid="upload-file-button"
                    >
                      <UploadCloud size={18} className="ml-2" />
                      رفع ملف PDF
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      onClick={handleApprove}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="approve-contract-button"
                    >
                      <CheckCircle2 size={18} className="ml-2" />
                      اعتماد العقد
                    </Button>
                  )}
                  {canSign && (
                    <Button
                      onClick={handleSign}
                      disabled={signing}
                      className="w-full bg-[#E94B7B] hover:bg-[#d43a6a]"
                      data-testid="sign-contract-button"
                    >
                      <PenTool size={18} className="ml-2" />
                      {signing ? 'جارٍ التوقيع...' : 'توقيع العقد'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-right">المسار الزمني</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 size={16} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">تم الإنشاء</p>
                      <p className="text-xs text-slate-500">
                        {new Date(contract.created_at).toLocaleString('ar-SA')}
                      </p>
                    </div>
                  </div>
                  {contract.reviewed_at && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} className="text-amber-700" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">تم إرساله للمراجعة</p>
                        <p className="text-xs text-slate-500">
                          {new Date(contract.reviewed_at).toLocaleString('ar-SA')}
                        </p>
                      </div>
                    </div>
                  )}
                  {contract.approved_at && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} className="text-emerald-700" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">تم الاعتماد</p>
                        <p className="text-xs text-slate-500">
                          {new Date(contract.approved_at).toLocaleString('ar-SA')}
                        </p>
                      </div>
                    </div>
                  )}
                  {contract.signed_at && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} className="text-indigo-700" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">تم التوقيع</p>
                        <p className="text-xs text-slate-500">
                          {new Date(contract.signed_at).toLocaleString('ar-SA')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">رفع ملف العقد</DialogTitle>
            <DialogDescription className="text-right">
              اختر ملف PDF لرفعه
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="file" className="text-right block">ملف PDF</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="text-right"
                data-testid="file-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleFileUpload}
                disabled={!selectedFile || uploading}
                className="flex-1 bg-[#E94B7B] hover:bg-[#d43a6a]"
                data-testid="upload-submit-button"
              >
                {uploading ? 'جارٍ الرفع...' : 'رفع الملف'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadDialogOpen(false);
                  setSelectedFile(null);
                }}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
